import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import type { DenseHeaderMetricModel } from '../../presentation/cards/types';

interface DenseHeaderMetricStripProps {
  items: DenseHeaderMetricModel[];
}

const DenseHeaderMetricStrip: React.FC<DenseHeaderMetricStripProps> = ({ items }) => {
  const theme = useTheme();

  const getDeltaTone = (item: DenseHeaderMetricModel): { color: string; icon: React.ReactNode } | null => {
    if (item.deltaValue == null || !item.deltaText) return null;
    const delta = item.deltaValue;
    const isPositive = delta >= 0;

    if (item.deltaDirection === 'neutral') {
      return {
        color: theme.palette.text.secondary,
        icon: isPositive ? <ArrowDropUpRoundedIcon sx={{ fontSize: 18 }} /> : <ArrowDropDownRoundedIcon sx={{ fontSize: 18 }} />,
      };
    }

    const isGood =
      (item.deltaDirection === 'up-good' && isPositive) ||
      (item.deltaDirection === 'down-good' && !isPositive);

    return {
      color: isGood ? '#15803d' : '#b42318',
      icon: isPositive ? <ArrowDropUpRoundedIcon sx={{ fontSize: 18 }} /> : <ArrowDropDownRoundedIcon sx={{ fontSize: 18 }} />,
    };
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        '&::-webkit-scrollbar': {
          height: 5,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(theme.palette.text.primary, 0.14),
          borderRadius: 999,
        },
      }}
    >
      {items.map((item) => {
        const deltaTone = getDeltaTone(item);
        const metricContent = (
          <Box
            sx={{
              px: 1.15,
              py: 0.4,
              minWidth: 98,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: theme.palette.text.secondary,
                lineHeight: 1.15,
                letterSpacing: 0,
                fontWeight: 300,
                fontSize: 10.25,
              }}
            >
              {item.label}
            </Typography>
            <Stack direction="row" spacing={0.55} alignItems="flex-end" sx={{ mt: 0.08 }}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 400,
                  lineHeight: 1,
                  color: theme.palette.mode === 'dark' ? '#f8fafc' : '#111827',
                  letterSpacing: 0,
                }}
              >
                {item.value}
              </Typography>
              {deltaTone && item.deltaText ? (
                <Stack
                  direction="row"
                  spacing={0.15}
                  alignItems="center"
                  sx={{
                    color: deltaTone.color,
                    lineHeight: 1,
                    mb: 0.02,
                  }}
                >
                  {deltaTone.icon}
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 400,
                      color: 'inherit',
                      lineHeight: 1,
                    }}
                  >
                    {item.deltaText.replace(/^[+-]/, '')}
                  </Typography>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        );

        if (!item.tooltipTitle && !item.tooltipDescription) {
          return <React.Fragment key={item.key}>{metricContent}</React.Fragment>;
        }

        return (
          <Tooltip
            key={item.key}
            title={
              <React.Fragment>
                {item.tooltipTitle ? (
                  <Typography variant="subtitle2">{item.tooltipTitle}</Typography>
                ) : null}
                {item.tooltipDescription ? (
                  <Typography variant="body2">{item.tooltipDescription}</Typography>
                ) : null}
              </React.Fragment>
            }
          >
            {metricContent}
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default DenseHeaderMetricStrip;
